import { graphql } from 'react-relay';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { isLocal as isLocalPillar } from '@confluence/ssr-utilities/entry-points/env.ts';

import { fetchORSForSSR } from '../entry-points/fetchORSForSSR.ts';
import { getInitialState } from '../entry-points/getInitialState.ts';
import { getSmartLinks } from '../entry-points/getSmartLinks.ts';

import type {
	preloadContentSmartLinksQuery$data,
	preloadContentSmartLinksQuery$variables,
} from './__generated__/preloadContentSmartLinksQuery.graphql.ts';
import type {
	preloadContentSmartLinksForDraftQuery$data,
	preloadContentSmartLinksForDraftQuery$variables,
} from './__generated__/preloadContentSmartLinksForDraftQuery.graphql.ts';
import {
	groupSmartLinksByHostname,
	mergeSmallThirdPartyBatches,
	splitLinksIntoBatches,
	getLinkType,
} from './smartLinkBatchHelpers.ts';
import type { SmartLinkType } from './getSmartLinks.ts';

declare global {
	interface Window {
		__SSR_SMARTLINKS__?: { [key: string]: any } | {};
	}
}

const SMART_LINK_INDIVIDUAL_TIMEOUT = 250;
const SMART_LINK_BATCH_TIMEOUT = 350;
const DEFAULT_MIN_BATCH_COUNT = 1;
const DEFAULT_MAX_BATCH_COUNT = 50;

const preloadContentSmartlinksQuery = async (
	contentId: string,
	isDraft: boolean = false,
): Promise<SmartLinkType[]> => {
	if (isDraft) {
		const smartLinkDraftRes = await query<
			preloadContentSmartLinksForDraftQuery$data,
			preloadContentSmartLinksForDraftQuery$variables
		>({
			query: graphql`
				query preloadContentSmartLinksForDraftQuery($contentId: ID!) {
					confluence_contentSmartLinksForDraft(id: $contentId) {
						nodes {
							__typename
							url
						}
					}
				}
			`,
			variables: {
				contentId,
			},
		});
		return getSmartLinks(smartLinkDraftRes, isDraft);
	}

	const smartLinksRes = await query<
		preloadContentSmartLinksQuery$data,
		preloadContentSmartLinksQuery$variables
	>({
		query: graphql`
			query preloadContentSmartLinksQuery($contentId: ID!) {
				contentSmartLinks(id: $contentId) {
					nodes {
						__typename
						url
					}
				}
			}
		`,
		variables: {
			contentId,
		},
	});
	return getSmartLinks(smartLinksRes, isDraft);
};

export const getSmartLinksStats = (
	orsCalls: any[],
	smartLinks: any[],
	resolvedSmartLinks: any[],
	fetchTimeout: Record<string, number>,
) => {
	const resolvedLinks = resolvedSmartLinks.reduce((acc, { status, body }) => {
		if (status === 504) {
			acc[body?.data?.url] = 'timeout';
		}
		if (status === 200) {
			acc[body?.data?.url] = 'resolved';
		}
		return acc;
	}, {});
	return {
		fetch: orsCalls.length,
		fetchTimeout,
		links: smartLinks.reduce((acc, { resourceUrl, appearance }) => {
			const linkType = getLinkType(resourceUrl);
			if (!acc?.[linkType]?.[appearance]) {
				acc[linkType] = { [appearance]: { total: 0, timeout: 0, resolved: 0 } };
			}
			acc[linkType][appearance].total++;
			if (resolvedLinks[resourceUrl]) {
				if (resolvedLinks[resourceUrl] === 'timeout') {
					acc[linkType][appearance].timeout++;
				}
				if (resolvedLinks[resourceUrl] === 'resolved') {
					acc[linkType][appearance].resolved++;
				}
			}
			return acc;
		}, {}),
	};
};

async function fetchORSForSSRWithTimeout(
	links: SmartLinkType[],
	timeout: number,
	batchTimeout: number,
	fetchTimeout: Record<string, number>,
) {
	if (links.length === 0) {
		return [];
	}

	const { customURLs, userContext } = getSSRRenderInputs();
	const objectResolverURL = isLocalPillar()
		? '/gateway/api/object-resolver/resolve/batch'
		: customURLs?.['object-resolver-service']!;

	const firstLink = links[0].resourceUrl;
	const type = getLinkType(firstLink);

	return timeGuard(
		async () => {
			try {
				const res = await fetchORSForSSR({
					// Despite the API has a timeout it does not respect it. It can easily go over time.
					url: `${objectResolverURL}?timeout=${timeout}`,
					body: JSON.stringify(links),
					userContext,
				});
				if (!res.ok) {
					throw new Error(`${res.status} ${res.statusText}`);
				}
				return res.json();
			} catch (e) {
				ssrPrepareErrorsClient.submitError(
					new Error(`Smart link content ORS call failed: ${e.message}`),
				);
			}
			return [];
		},
		() => {
			fetchTimeout[type] = fetchTimeout[type] || 0;
			fetchTimeout[type]++;
			return [];
		},
		batchTimeout,
	);
}

/*
 * preloadContentSmartLinks tracks the entire preloaded chain of queries needed to SSR Content Smart Links. This consists of two calls:
 * 1. preloadContentSmartlinksQuery, a GQL call to fetch SmartLinks URLs and
 * 2. A call to ORS to resolve the metadata, which uses the links returned from the previous call
 * The entirety of this chain is currently best effort, which means that SmartLinks will either resolve ALL
 * links by the time the longest preloaded query resolves or NONE.
 *
 * If a timeout is provided, Smartlinks will try and resolve as many links as possible
 * within the provided timeout.
 */

export const preloadContentSmartLinks = async (contentId: string, isDraft: boolean = false) => {
	const smartLinks = await preloadContentSmartlinksQuery(contentId, isDraft);
	if (!smartLinks?.length) {
		return;
	}

	// How long a single ORS resolution can take
	const timeout = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_max_time',
		SMART_LINK_INDIVIDUAL_TIMEOUT,
	);

	// How long a batch of ORS resolution can take
	const batchTimeout = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_batch_max_time',
		SMART_LINK_BATCH_TIMEOUT,
	);

	// How many links should be packed in a batch
	const min = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_min_batch_count',
		DEFAULT_MIN_BATCH_COUNT,
	);
	const max = Math.max(
		min,
		StatsigConfigurations.getNumber(
			'confluence_ssr_content_smart_link_max_batch_count',
			DEFAULT_MAX_BATCH_COUNT,
		),
	);

	await SSRMeasures.run('ssr-app/prepare/preloadQuery/fetch:contentSmartLinksORS', async () => {
		const orsCalls: Promise<any>[] = [];
		const fetchTimeout = {};
		let smartLinksBatches = groupSmartLinksByHostname(smartLinks);
		smartLinksBatches = mergeSmallThirdPartyBatches(smartLinksBatches, min);
		smartLinksBatches = splitLinksIntoBatches(smartLinksBatches, max);

		for (const smartLinksBatch of smartLinksBatches) {
			orsCalls.push(
				fetchORSForSSRWithTimeout(smartLinksBatch, timeout, batchTimeout, fetchTimeout),
			);
		}

		// eslint-disable-next-line check-preloaders/no-promise-all
		const resolvedSmartLinks = (await Promise.all(orsCalls)).flat();

		// Format response to fit SmartCard Provider storeOptions initial state
		// Save correctly formatted response to window obj so it can be used on SSR and SPA (no duplicate calls)
		window['__SSR_SMARTLINKS__'] = getInitialState(resolvedSmartLinks);
		window['__SSR_SMARTLINKS_STATS__'] = getSmartLinksStats(
			orsCalls,
			smartLinks,
			resolvedSmartLinks,
			fetchTimeout,
		);
	});
};
